import React from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import moment from 'moment-timezone';
import { Card } from '../../cards';
import { SquareButton, ButtonRow, ActionButton } from '../../JCButtons';
import colors from '../../../config/colors';
import routes from '../../../navigation/routes';

/**
 * ComputerPalView - Device view for Computer Pal (deviceType "4")
 * Computer monitoring and remote reboot
 */
function ComputerPalView({
    data,
    deviceData,
    isConnected,
    newSettings5,
    auth,
    appTimezone,
    convertTimezone,
    toggleModal,
    setVisibleTrigger2,
    navigation,
    route,
    setUpdateNeededHigherLevel,
}) {
    const deviceInfo = data?.[0];
    const realtimeData = deviceInfo?.realtimeData ? JSON.parse(deviceInfo.realtimeData) : {};

    const formatUptime = (totalSeconds) => {
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${days.toString().padStart(2, '0')}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <>
            {/* Device Metrics Card */}
            <Card>
                <View style={styles.metricsContainer}>
                    <MetricRow
                        label="Last Reboot"
                        value={`${convertTimezone(deviceInfo?.lastResetTime)} (${moment().tz(appTimezone).format('z')})`}
                    />
                    <MetricRow
                        label="Cloud Notifications"
                        value={deviceInfo?.offlineCloudNotificationDelay == 0 ? "Not Enabled" : "Enabled"}
                    />

                    {isConnected && (
                        <>
                            {realtimeData?.U && (
                                <MetricRow
                                    label="Uptime"
                                    value={formatUptime(deviceData?.uptime || realtimeData?.U)}
                                />
                            )}
                            <MetricRow
                                label="CPU"
                                value={`${deviceData?.cpu || realtimeData?.CPU} %`}
                            />
                            <MetricRow
                                label="RAM"
                                value={`${deviceData?.ram || realtimeData?.RAM} %`}
                            />
                            <MetricRow
                                label="Speed Up"
                                value={`${deviceData?.speedu || realtimeData?.SpeedU} Mbps`}
                            />
                            <MetricRow
                                label="Speed Down"
                                value={`${deviceData?.speedd || realtimeData?.SpeedD} Mbps`}
                            />
                        </>
                    )}
                </View>

                {/* Device Icon */}
                <Image
                    source={require('../../../assets/compPal.png')}
                    style={styles.deviceIcon}
                />
            </Card>

            {/* Actions */}
            <Card style={styles.actionsCard}>
                <Text style={styles.sectionTitle}>Device Management</Text>
                <ButtonRow>
                    <SquareButton
                        title="View/Change Settings"
                        onPress={() => navigation.navigate(routes.CHANGESETTINGSCOMPUTERPAL, { item: route.params.item, changedDevices: setUpdateNeededHigherLevel })}
                        icon="cogs"
                        color={colors.primary}
                        disabled={auth.userPermissions.role !== 'admin'}
                    />
                    <SquareButton
                        title="View Event History"
                        onPress={() => navigation.navigate(routes.DASHBOARD, { item: route.params.item, changedDevices: setUpdateNeededHigherLevel })}
                        icon="history"
                        color={colors.primary}
                    />
                    <SquareButton
                        title="More Device Details"
                        onPress={() => navigation.navigate(routes.PRESENTSETTINGS, { item: route.params.item })}
                        icon="search-plus"
                        color={colors.primary}
                    />
                </ButtonRow>
                {newSettings5 && (
                    <ButtonRow>
                        <SquareButton
                            title="Trigger Reboot"
                            onPress={toggleModal}
                            icon="retweet"
                            color="red"
                            disabled={auth.userPermissions.role !== 'admin'}
                        />
                        <SquareButton
                            title="Deregister Device"
                            onPress={() => setVisibleTrigger2(true)}
                            icon="trash"
                            color={colors.primary}
                            disabled={auth.userPermissions.role !== 'admin'}
                        />
                    </ButtonRow>
                )}
                {!newSettings5 && (
                    <ButtonRow>
                        <ActionButton
                            title="Deregister Device"
                            onPress={() => setVisibleTrigger2(true)}
                            icon="trash"
                            color={colors.primary}
                            disabled={auth.userPermissions.role !== 'admin'}
                        />
                    </ButtonRow>
                )}
            </Card>
        </>
    );
}

function MetricRow({ label, value }) {
    return (
        <View style={styles.metricRow}>
            <Text style={styles.metricLabel}>{label}</Text>
            <Text style={styles.metricValue}>{value}</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    metricsContainer: {
        position: 'relative',
        paddingBottom: 35, // Make room for absolute-positioned device icon
    },
    metricRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    metricLabel: {
        fontWeight: 'bold',
        color: colors.dark,
        fontSize: 14,
    },
    metricValue: {
        color: colors.medium,
        fontSize: 14,
    },
    deviceIcon: {
        position: 'absolute',
        bottom: 8,
        right: 8,
        width: 80,
        height: 25,
    },
    actionsCard: {
        paddingVertical: 12,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: colors.dark,
        marginBottom: 8,
        textAlign: 'center',
    },
});

export default ComputerPalView;
